import { useEffect, useState } from "react";
import { Eye, Loader2, Palette, Play, Sparkles, Trash2, Undo2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { apercuLocalTheme, CURSEURS } from "../ThemeSaison";

// Carte « Thème du site » (Admin → Aperçu — demande du propriétaire, 2026-10-27) :
// thèmes saisonniers à période RÉCURRENTE (JJ-MM jour-mois à la française,
// chaque année, chevauchement du jour de l'an supporté) — ex. Halloween
// 18-10 → 01-11 12 h, Noël 15-11 → 06-01.
// « APERÇU » fait VOIR le thème sur place (palette + décor, cet appareil
// seulement, sans toucher au site) ; « APPLIQUER MAINTENANT » l'affiche à tous
// peu importe la période, jusqu'à « Revenir au calendrier ». Toutes les options
// de décor rendent le site plus festif (neige dense, feuilles multicolores,
// étoiles scintillantes, bokeh coloré, guirlande d'ampoules, confettis).
const PALETTE_CLES = [
  ["fond", "Fond"],
  ["cartes", "Cartes"],
  ["accent", "Accent"],
  ["accent_clair", "Accent pâle"],
  ["texte", "Texte"],
];
// Décors groupés PROPREMENT (demande du propriétaire, 2026-10-27 :
// « réorganiser les listes de décors plus proprement ») — trois familles +
// le réglage de QUANTITÉ (« pluie légère / moyenne / forte »)
const DECOR_GROUPES = [
  ["Pluie festive", [["neige", "Neige"], ["feuilles", "Feuilles d'automne"],
                     ["erables", "Feuilles d'érable"], ["confettis", "Confettis"],
                     ["ballons", "Ballons qui montent"]]],
  ["Fêtes", [["bonbons", "Bonbons"], ["coeurs", "Cœurs"],
             ["paques", "Œufs et lapins de Pâques"], ["trefles", "Trèfles"]]],
  ["Ambiance", [["etoiles", "Étoiles"], ["bokeh", "Bokeh coloré"],
                ["guirlande", "Guirlande d'ampoules"]]],
];
// Plusieurs choix de pluie (demande du propriétaire, 2026-10-27) — jusqu'à
// « Tempête », qui fait × 3 la pluie forte
const DENSITES = [["tres-legere", "Bruine (très légère)"], ["legere", "Pluie légère"],
                  ["moyenne", "Pluie moyenne"], ["forte", "Pluie forte"],
                  ["tempete", "Tempête (× 3 la forte)"]];
// Le décor d'un thème est une LISTE (cases à cocher — plusieurs à la fois) ;
// une vieille valeur seule (chaîne) est lue comme une liste d'un élément
const decorsDe = (t) =>
  Array.isArray(t.decor) ? t.decor : (t.decor && t.decor !== "aucun" ? [t.decor] : []);

export default function ThemeSaisonCard({ themes, forceId, onChange, onForceChange }) {
  const [liste, setListe] = useState(themes || []);
  const [dirty, setDirty] = useState(false);   // modifs locales pas encore enregistrées
  // Les réglages arrivent en ASYNC : sans cette synchro, la carte montait avec
  // une liste VIDE et « Enregistrer » écrasait les thèmes enregistrés
  // (bogue rapporté par le propriétaire, 2026-10-27 : « les thèmes ne
  // s'enregistrent pas »). On ne remplace pas la liste pendant une retouche.
  useEffect(() => { if (!dirty) setListe(themes || []); }, [themes]);
  const [fete, setFete] = useState("");
  const [iaBusy, setIaBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState("");      // id du thème dont Gemini dessine un décor
  const [imgSujet, setImgSujet] = useState({});    // {index: description du décor à dessiner}
  const [gemBusy, setGemBusy] = useState("");      // "index:curseur" | "index:fond" en génération
  // GALERIE des décors dessinés par Gemini (demande du propriétaire, 2026-10-27 :
  // « garder tous les décors dans une bibliothèque réutilisable d'un thème à
  // l'autre ») — chargée à la première ouverture, un clic ajoute/retire au thème
  const [galerie, setGalerie] = useState(null);    // null = pas encore chargée
  const [galerieIdx, setGalerieIdx] = useState(null);  // rangée de thème ouverte
  const chargerGalerie = async () => {
    try {
      const { data } = await api.get("/admin/themes-saison/decors");
      setGalerie(data.decors || []);
    } catch { setGalerie([]); }
  };
  const [saveBusy, setSaveBusy] = useState(false);
  const [apercu, setApercu] = useState(null);
  // THÈME EN VIGUEUR, dit clairement (demande du propriétaire, 2026-10-27 :
  // « identifier clairement le thème utilisé ») — lu au montage et relu
  // après chaque enregistrement / « Appliquer maintenant » / retour au
  // calendrier (le cache public est invalidé par ces actions)
  const [enVigueur, setEnVigueur] = useState(undefined);   // undefined = lecture…
  const lireEnVigueur = () =>
    api.get("/theme-saison")
      .then(({ data }) => setEnVigueur(data?.actif
        ? { nom: data.theme?.nom, force: !!data.force, palette: data.theme?.palette || {} }
        : (data?.sans_theme ? { sansTheme: true } : null)))
      .catch(() => {});
  useEffect(() => { lireEnVigueur(); }, []);   // thème en cours de VISUALISATION (cet appareil seulement)
  const [forceBusy, setForceBusy] = useState("");

  const maj = (i, patch) => { setDirty(true); setListe((ls) => ls.map((t, j) => (j === i ? { ...t, ...patch } : t))); };
  const majCouleur = (i, cle, v) => {
    setDirty(true);
    setListe((ls) => ls.map((t, j) => (j === i ? { ...t, palette: { ...(t.palette || {}), [cle]: v } } : t)));
  };

  const creerIA = async () => {
    const f = fete.trim();
    if (f.length < 2 || iaBusy) { if (!f) toast.error("Nommez la fête (ex. : Halloween, Noël, Pâques…)"); return; }
    setIaBusy(true);
    try {
      const { data } = await api.post("/admin/themes-saison/ia", { fete: f });
      setListe(data.themes);
      onChange?.(data.themes);
      setFete("");
      toast.success(`Thème « ${data.theme.nom} » composé par Gemini (${data.theme.debut} → ${data.theme.fin}) — retouchez-le au besoin puis « Enregistrer les thèmes ».`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setIaBusy(false); }
  };

  // DÉCOR DESSINÉ PAR GEMINI (demande du propriétaire, 2026-10-27 : « images
  // de décor générées ») : Nano Banana produit l'image, elle s'ajoute aux
  // décors du thème (elle TOMBE pendant la fête) — « Enregistrer » pour garder
  const genererDecor = async (i, t) => {
    const sujet = (imgSujet[i] || "").trim() || t.nom;
    if (imgBusy) return;
    setImgBusy(t.id || String(i));
    try {
      const { data } = await api.post("/admin/themes-saison/decor-image", { sujet });
      maj(i, { decor: [...decorsDe(t), data.decor] });
      setGalerie(null);  // la bibliothèque se rechargera avec le nouveau décor
      toast.success(`Décor « ${sujet} » dessiné par Gemini et ajouté au thème — « Enregistrer les thèmes » pour le garder.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImgBusy(""); }
  };

  // CURSEUR et FOND D'ACCUEIL dessinés par Gemini (demande du propriétaire,
  // 2026-10-27) : un clic par thème — le curseur remplace la flèche de la
  // souris pendant la fête, le fond devient le voile d'ambiance du site
  const genererImage = async (i, t, genre) => {
    if (gemBusy) return;
    setGemBusy(`${i}:${genre}`);
    try {
      const sujet = genre === "fond" ? `ambiance festive « ${t.nom} »` : t.nom;
      const { data } = await api.post(
        `/admin/themes-saison/${genre === "fond" ? "fond-image" : "curseur-image"}`, { sujet });
      maj(i, genre === "fond" ? { fond_img: data.path } : { curseur_img: data.path });
      toast.success(genre === "fond"
        ? "Fond d'ambiance dessiné par Gemini — « Enregistrer les thèmes » pour le garder."
        : "Curseur dessiné par Gemini — « Enregistrer les thèmes » pour le garder.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGemBusy(""); }
  };

  const enregistrer = async () => {
    if (saveBusy) return;
    setSaveBusy(true);
    try {
      await api.patch("/admin/settings", { themes_saison: liste });
      onChange?.(liste);
      setDirty(false);
      lireEnVigueur();
      toast.success("Thèmes enregistrés — le thème dont la période est en cours s'applique au site dans la minute.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaveBusy(false); }
  };

  // APERÇU VISUEL (demande du propriétaire, 2026-10-27) : le thème demandé est
  // posé par le composant global (palette + décor + curseur), À CET APPAREIL
  // seulement — le vrai thème se tait pendant l'aperçu (UNE seule couche de
  // décor, plus de feuilles superposées à la neige) ; « Quitter l'aperçu »
  // redonne la main au thème réellement en vigueur
  const voirApercu = (t) => {
    apercuLocalTheme(t);
    setApercu(t);
  };
  const quitterApercu = () => {
    apercuLocalTheme(null);
    setApercu(null);
  };

  // APPLIQUER MAINTENANT (demande du propriétaire, 2026-10-27) : le thème
  // s'affiche à TOUS peu importe la période, jusqu'à « Revenir au calendrier »
  const forcer = async (t) => {
    if (forceBusy) return;
    setForceBusy(t.id);
    try {
      await api.patch("/admin/settings", { theme_force_id: t.id });
      onForceChange?.(t.id);
      lireEnVigueur();
      toast.success(`Thème « ${t.nom} » appliqué MAINTENANT à tout le site (dans la minute) — pensez à « Revenir au calendrier » après la fête.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setForceBusy(""); }
  };
  const liberer = async () => {
    if (forceBusy) return;
    setForceBusy("calendrier");
    try {
      await api.patch("/admin/settings", { theme_force_id: null });
      onForceChange?.(null);
      lireEnVigueur();
      toast.success("Retour au calendrier — les thèmes s'appliquent selon leurs périodes.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setForceBusy(""); }
  };

  // SANS THÈME forcé (demande du propriétaire, 2026-10-27 : « garder l'option
  // sans thème ») : le site garde ses couleurs noires et or MÊME pendant une
  // période de fête — jusqu'à « Revenir au calendrier »
  const sansTheme = async () => {
    if (forceBusy) return;
    setForceBusy("aucun");
    try {
      await api.patch("/admin/settings", { theme_force_id: "aucun" });
      onForceChange?.("aucun");
      lireEnVigueur();
      toast.success("Sans thème appliqué à tout le site — noir et or habituels, même pendant une fête. « Revenir au calendrier » rend la main aux périodes.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setForceBusy(""); }
  };

  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-8" data-testid="theme-saison-card">
      {/* Bandeau d'APERÇU : visible seulement chez l'admin qui visualise —
          le décor/palette/curseur sont posés par le composant global */}
      {apercu && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-3 bg-surface border border-gold rounded-full px-5 py-2 shadow-2xl shadow-black/70"
          data-testid="theme-apercu-bandeau">
          <Eye className="w-4 h-4 text-gold" />
          <span className="text-sm">Aperçu du thème <strong className="text-gold">« {apercu.nom} »</strong> — chez vous seulement</span>
          <button type="button" onClick={quitterApercu} data-testid="theme-apercu-quitter"
            className="inline-flex items-center gap-1.5 text-xs bg-gold text-ink font-bold rounded-full px-3 py-1 hover:bg-champagne transition-colors">
            <Undo2 className="w-3.5 h-3.5" /> Quitter l'aperçu
          </button>
        </div>
      )}

      <p className="font-semibold flex items-center gap-2 flex-wrap">
        <Palette className="w-4 h-4 text-gold" /> Thème du site (fêtes et saisons)
        <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
      </p>
      <p className="text-xs text-ivory/70 mt-1 mb-4">
        Le thème dont la <strong className="text-ivory/90">période est en cours</strong> s'applique TOUT SEUL au site
        (couleurs + animation festive) et se retire à la fin — ex. : Halloween du 18 octobre au 1er novembre 12 h,
        Noël du 15 novembre au 6 janvier. Périodes au format <strong className="text-ivory/90">JJ-MM</strong> (jour-mois, à la française — ex. : 18-10 = 18 octobre), reprises chaque année.
        <strong className="text-ivory/90"> Aperçu</strong> : le voir ici même, chez vous seulement.
        <strong className="text-ivory/90"> Appliquer maintenant</strong> : l'afficher à tous immédiatement.
      </p>

      {/* THÈME EN VIGUEUR, identifié clairement (demande du propriétaire,
          2026-10-27) : nom + comment il a été appliqué (calendrier ou de
          force) + pastilles de sa palette — relu après chaque action */}
      <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 mb-5 ${
          enVigueur ? "border-gold/60 bg-gold/10" : "border-ivory/15 bg-ink/60"}`}
        data-testid="theme-en-vigueur">
        {enVigueur === undefined ? (
          <span className="text-xs text-ivory/50">Lecture du thème en vigueur…</span>
        ) : enVigueur?.sansTheme ? (
          <>
            <span className="text-sm">
              <strong className="text-ivory/85">Sans thème</strong>
              <span className="text-ivory/60"> — appliqué DE FORCE : noir et or habituels, même pendant une fête.</span>
            </span>
            <button type="button" onClick={liberer} disabled={forceBusy === "calendrier"}
              data-testid="theme-sanstheme-calendrier"
              className="ml-auto inline-flex items-center gap-1.5 text-[11px] border border-ivory/30 text-ivory/70 rounded-full px-3 py-1 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-60">
              {forceBusy === "calendrier" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
              Revenir au calendrier
            </button>
          </>
        ) : enVigueur ? (
          <>
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
            </span>
            <span className="text-sm">
              En ce moment sur le site : <strong className="text-gold">« {enVigueur.nom} »</strong>
              <span className="text-ivory/60"> — {enVigueur.force
                ? "appliqué DE FORCE (« Appliquer maintenant ») jusqu'à « Revenir au calendrier »"
                : "appliqué par le calendrier, selon sa période"}</span>
            </span>
            {enVigueur.palette?.accent && (
              <span className="flex items-center gap-1 ml-auto" title="Palette du thème en vigueur">
                {["fond", "cartes", "accent", "accent_clair", "texte"].map((k) => (
                  <span key={k} className="w-4 h-4 rounded-full border border-ivory/25"
                    style={{ background: enVigueur.palette[k] || "transparent" }} />
                ))}
              </span>
            )}
          </>
        ) : (
          <span className="text-sm text-ivory/60">
            Aucun thème en ce moment — le site garde ses couleurs habituelles (noir et or).
          </span>
        )}
      </div>

      {/* Création par GEMINI (demande du propriétaire, 2026-10-27 : « thèmes
          du site fournis par mon IA Gemini ») */}
      <div className="flex flex-wrap items-center gap-3 mb-5 bg-ink border border-gold/20 rounded-lg p-4">
        <Sparkles className="w-4 h-4 text-gold shrink-0" />
        <input type="text" value={fete} onChange={(e) => setFete(e.target.value)}
          data-testid="theme-ia-fete" placeholder="Fête à thématiser (ex. : Saint-Valentin, Pâques, Saint-Jean…)"
          className="flex-1 min-w-[240px] bg-surface border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
        <button type="button" onClick={creerIA} disabled={iaBusy} data-testid="theme-ia-creer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-60">
          {iaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {iaBusy ? "Gemini compose…" : "Créer le thème avec Gemini"}
        </button>
        {/* SANS THÈME — option conservée (demande du propriétaire, 2026-10-27) :
            force le noir et or habituels même pendant une période de fête */}
        {forceId !== "aucun" && (
          <button type="button" onClick={sansTheme} disabled={!!forceBusy} data-testid="theme-sans-theme"
            title="Aucun thème pour tout le monde — le site garde son look habituel, même pendant une fête"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-ivory/25 text-ivory/70 hover:border-ivory/60 hover:text-ivory transition-colors disabled:opacity-60">
            {forceBusy === "aucun" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
            Sans thème
          </button>
        )}
      </div>

      {liste.length === 0 ? (
        <p className="text-xs text-ivory/50" data-testid="theme-saison-vide">
          Aucun thème pour l'instant — créez « Halloween » et « Noël » avec l'IA ci-dessus (leurs dates sont déjà réglées comme demandé).
        </p>
      ) : (
        <div className="space-y-4" data-testid="theme-saison-liste">
          {liste.map((t, i) => (
            <div key={t.id || i} className={`border rounded-lg p-4 ${forceId === t.id ? "border-gold bg-gold/5" : "border-ivory/10"}`}
              data-testid={`theme-saison-${t.id || i}`}>
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={t.actif !== false}
                    onChange={(e) => maj(i, { actif: e.target.checked })}
                    data-testid={`theme-actif-${i}`} className="accent-[#d4af37]" />
                  <strong>{t.nom}</strong>
                </label>
                {forceId === t.id && (
                  <span className="text-[9px] uppercase tracking-widest bg-gold text-ink font-bold rounded-full px-2 py-0.5"
                    data-testid={`theme-force-${i}`}>en vigueur — forcé</span>
                )}
                <span className="text-[11px] text-ivory/50">du</span>
                <input type="text" value={t.debut || ""} onChange={(e) => maj(i, { debut: e.target.value })}
                  data-testid={`theme-debut-${i}`} placeholder="JJ-MM" maxLength={5} title="Début, jour-mois (ex. : 18-10 = 18 octobre)"
                  className="w-16 bg-ink border border-ivory/15 rounded px-2 py-1 text-xs text-center focus:border-gold focus:outline-none" />
                <span className="text-[11px] text-ivory/50">au</span>
                <input type="text" value={t.fin || ""} onChange={(e) => maj(i, { fin: e.target.value })}
                  data-testid={`theme-fin-${i}`} placeholder="JJ-MM" maxLength={5} title="Fin, jour-mois (ex. : 01-11 = 1er novembre)"
                  className="w-16 bg-ink border border-ivory/15 rounded px-2 py-1 text-xs text-center focus:border-gold focus:outline-none" />
                <input type="text" value={t.fin_heure || ""} onChange={(e) => maj(i, { fin_heure: e.target.value })}
                  data-testid={`theme-fin-heure-${i}`} placeholder="fin (ex. 12:00)" maxLength={5} title="Heure de fin le dernier jour (optionnel)"
                  className="w-20 bg-ink border border-ivory/15 rounded px-2 py-1 text-xs text-center focus:border-gold focus:outline-none" />
                {/* CURSEUR de la fête (demande du propriétaire, 2026-10-27) :
                    la flèche de la souris devient une forme choisie dans la
                    liste (citrouille, fantôme, cadeau, canne de bonbon…) */}
                <select value={t.curseur || "aucun"} onChange={(e) => maj(i, { curseur: e.target.value })}
                  data-testid={`theme-curseur-${i}`} title="Forme de la souris pendant ce thème"
                  className="bg-ink border border-ivory/15 rounded px-2 py-1 text-xs focus:border-gold focus:outline-none">
                  {CURSEURS.map(([v, l]) => <option key={v} value={v}>Curseur : {l}</option>)}
                </select>
                <button type="button" onClick={() => voirApercu(t)}
                  data-testid={`theme-apercu-${i}`} title="Voir ce thème ici même (chez vous seulement)"
                  className="inline-flex items-center gap-1.5 text-[11px] border border-sky-400/40 text-sky-300 rounded-full px-3 py-1 hover:bg-sky-400/10 transition-colors">
                  <Eye className="w-3.5 h-3.5" /> Aperçu
                </button>
                {forceId === t.id ? (
                  <button type="button" onClick={liberer} disabled={forceBusy === "calendrier"}
                    data-testid={`theme-calendrier-${i}`} title="Le calendrier reprend la main"
                    className="inline-flex items-center gap-1.5 text-[11px] border border-ivory/30 text-ivory/70 rounded-full px-3 py-1 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-60">
                    {forceBusy === "calendrier" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                    Revenir au calendrier
                  </button>
                ) : (
                  <button type="button" onClick={() => forcer(t)} disabled={!!forceBusy}
                    data-testid={`theme-forcer-${i}`} title="Afficher ce thème à tous, peu importe la période"
                    className="inline-flex items-center gap-1.5 text-[11px] bg-gold text-ink font-semibold rounded-full px-3 py-1 hover:bg-champagne transition-colors disabled:opacity-60">
                    {forceBusy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    Appliquer maintenant
                  </button>
                )}
                <button type="button" onClick={() => { setDirty(true); setListe((ls) => ls.filter((_, j) => j !== i)); }}
                  data-testid={`theme-suppr-${i}`} title="Retirer ce thème (puis « Enregistrer les thèmes »)"
                  className="ml-auto text-ivory/40 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {/* DÉCORS en CASES À COCHER groupées proprement (demande du
                  propriétaire, 2026-10-27) : PLUSIEURS à la fois — ex. bonbons
                  + feuilles ensemble ; tout décoché = aucun décor. Quantité :
                  « pluie légère / moyenne / forte » */}
              <div className="mt-3 bg-ink/60 border border-ivory/10 rounded-lg px-4 py-3"
                data-testid={`theme-decors-${i}`}>
                <div className="flex flex-wrap gap-x-8 gap-y-3">
                  {DECOR_GROUPES.map(([groupe, items]) => (
                    <div key={groupe}>
                      <p className="text-[9px] uppercase tracking-widest text-gold/70 mb-1.5">{groupe}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                        {items.map(([v, l]) => (
                          <label key={v} className="inline-flex items-center gap-1 text-[11px] text-ivory/75 cursor-pointer hover:text-gold transition-colors">
                            <input type="checkbox" checked={decorsDe(t).includes(v)}
                              onChange={() => maj(i, { decor: decorsDe(t).includes(v) ? decorsDe(t).filter((d) => d !== v) : [...decorsDe(t), v] })}
                              data-testid={`theme-decor-${i}-${v}`} className="accent-[#d4af37]" />
                            {l}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-gold/70 mb-1.5">Quantité</p>
                    <select value={t.densite || "moyenne"} onChange={(e) => maj(i, { densite: e.target.value })}
                      data-testid={`theme-densite-${i}`} title="Dose la quantité qui tombe (ou monte)"
                      className="bg-ink border border-ivory/15 rounded px-2 py-1 text-xs focus:border-gold focus:outline-none">
                      {DENSITES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  {/* DÉCORS DESSINÉS PAR GEMINI (demande du propriétaire,
                      2026-10-27 : « images de décor générées ») — l'image TOMBE
                      pendant le thème ; la × la retire de la liste */}
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-gold/70 mb-1.5">Dessinés par Gemini</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {decorsDe(t).filter((d) => d.startsWith("img:")).map((d) => (
                        <span key={d} className="relative inline-block" title="Décor dessiné par Gemini — la × le retire du thème">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.slice(4)}`} alt="Décor Gemini"
                            className="w-9 h-9 rounded-lg border border-gold/40 object-cover bg-black" />
                          <button type="button" onClick={() => maj(i, { decor: decorsDe(t).filter((x) => x !== d) })}
                            data-testid={`theme-decor-img-retirer-${i}-${d.slice(-8)}`}
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center hover:bg-red-400 transition-colors">
                            ×
                          </button>
                        </span>
                      ))}
                      <input type="text" value={imgSujet[i] || ""}
                        onChange={(e) => setImgSujet((s) => ({ ...s, [i]: e.target.value }))}
                        placeholder={`ex. flocon doré, cœur…`} maxLength={100}
                        data-testid={`theme-decor-img-sujet-${i}`}
                        className="w-40 bg-surface border border-ivory/15 rounded px-2 py-1 text-[11px] focus:border-gold focus:outline-none" />
                      <button type="button" onClick={() => genererDecor(i, t)} disabled={!!imgBusy}
                        data-testid={`theme-decor-img-generer-${i}`}
                        title="Gemini dessine un décor sur mesure et l'ajoute à ce thème (il tombera pendant la fête)"
                        className="inline-flex items-center gap-1 text-[11px] bg-gold text-ink font-semibold rounded-full px-2.5 py-1 hover:bg-champagne transition-colors disabled:opacity-60">
                        {imgBusy === (t.id || String(i)) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Générer
                      </button>
                      {/* BIBLIOTHÈQUE des décors dessinés — réutilisables d'un
                          thème à l'autre (demande du propriétaire, 2026-10-27) */}
                      <button type="button" data-testid={`theme-galerie-${i}`}
                        onClick={() => { setGalerieIdx(galerieIdx === i ? null : i); if (!galerie) chargerGalerie(); }}
                        className="inline-flex items-center gap-1 text-[11px] border border-ivory/25 text-ivory/65 rounded-full px-2.5 py-1 hover:border-gold/50 hover:text-gold transition-colors">
                        <Palette className="w-3 h-3" /> Bibliothèque
                      </button>
                      {galerieIdx === i && (
                        <div className="w-full flex flex-wrap items-center gap-1.5 mt-1" data-testid={`theme-galerie-panel-${i}`}>
                          {(galerie || []).length === 0 && (
                            <span className="text-[10px] text-ivory/40">Aucun décor dessiné pour l'instant — « Générer » en crée un.</span>
                          )}
                          {(galerie || []).map((g) => {
                            const cle = `img:${g.path}`;
                            const actif = decorsDe(t).includes(cle);
                            return (
                              <button key={g.path} type="button"
                                title={actif ? `${g.nom} — dans ce thème, cliquez pour retirer` : `${g.nom} — cliquez pour ajouter à ce thème`}
                                onClick={() => maj(i, { decor: actif ? decorsDe(t).filter((x) => x !== cle) : [...decorsDe(t), cle] })}
                                data-testid={`theme-galerie-item-${i}-${g.path.slice(-8)}`}
                                className={`rounded-lg overflow-hidden border transition-all ${actif ? "border-gold ring-2 ring-gold/60" : "border-ivory/15 opacity-70 hover:opacity-100"}`}>
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`} alt={g.nom}
                                  className="w-9 h-9 object-cover bg-black" />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {PALETTE_CLES.map(([cle, label]) => (
                  <label key={cle} className="inline-flex items-center gap-1.5 text-[11px] text-ivory/60">
                    <input type="color" value={(t.palette || {})[cle] || "#111111"}
                      onChange={(e) => majCouleur(i, cle, e.target.value.toUpperCase())}
                      data-testid={`theme-couleur-${i}-${cle}`}
                      className="w-7 h-7 rounded border border-ivory/20 bg-transparent cursor-pointer" />
                    {label}
                  </label>
                ))}
              </div>
              {/* CURSEUR et FOND D'ACCUEIL dessinés par Gemini (demande du
                  propriétaire, 2026-10-27) : la × retire l'image du thème —
                  le curseur de la liste reprend alors le relais */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                {[["curseur", "curseur_img", "Curseur", "La souris prend cette forme pendant la fête", "w-6 h-6"],
                  ["fond", "fond_img", "Fond d'accueil", "Grand voile d'ambiance sur tout le site pendant la fête", "w-12 h-7"]].map(([genre, champ, label, titre, taille]) => (
                  <div key={genre} className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-ivory/50">{label}</span>
                    {t[champ] ? (
                      <span className="relative inline-block" title="Dessiné par Gemini — la × le retire du thème">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${t[champ]}`} alt={label}
                          className={`${taille} rounded border border-gold/40 object-cover bg-black`} />
                        <button type="button" onClick={() => maj(i, { [champ]: "" })}
                          data-testid={`theme-${genre}-img-retirer-${i}`}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center hover:bg-red-400 transition-colors">
                          ×
                        </button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => genererImage(i, t, genre)} disabled={!!gemBusy}
                        data-testid={`theme-${genre}-img-generer-${i}`} title={`${titre} — dessiné par Gemini`}
                        className="inline-flex items-center gap-1 text-[11px] border border-gold/30 text-gold rounded-full px-2.5 py-1 hover:border-gold/60 transition-colors disabled:opacity-60">
                        {gemBusy === `${i}:${genre}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Gemini
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={enregistrer} disabled={saveBusy} data-testid="theme-saison-save"
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-60">
        {saveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}
        Enregistrer les thèmes
      </button>
    </div>
  );
}
